"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import PhoneInput, { isValidPhoneNumber, formatPhoneNumberIntl, type Value } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { checkSmsVerifyStatus, initiateSmsVerify, type InitiateVerifyResult } from "@/lib/sms";
type Step = "phone" | "waiting";
interface SmsCodeVerifyFlowProps {
  onVerified: (phoneNumber: string) => void;
  onError?: (message: string) => void;
  onPhoneChange?: (phoneNumber: string) => void;
  disabled?: boolean;
}
const POLL_INTERVAL_MS = 3000;
const OTP_EXPIRATION_MS = 10 * 60 * 1000;
export function SmsCodeVerifyFlow({
  onVerified,
  onError,
  onPhoneChange,
  disabled
}: SmsCodeVerifyFlowProps) {
  const [phoneInput, setPhoneInput] = useState<Value | undefined>();
  const [step, setStep] = useState<Step>("phone");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyData, setVerifyData] = useState<InitiateVerifyResult | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const expiryRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    return () => {
      clearInterval(cooldownRef.current);
      clearInterval(pollRef.current);
      clearTimeout(expiryRef.current);
    };
  }, []);
  const startCooldown = useCallback(() => {
    setCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);
  const stopPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = undefined;
    clearTimeout(expiryRef.current);
    expiryRef.current = undefined;
  }, []);
  const startPolling = useCallback((phone: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const result = await checkSmsVerifyStatus(phone);
        if (result.status === "verified") {
          stopPolling();
          onVerified(phone);
          setPhoneInput(undefined);
          setVerifyData(null);
          setStep("phone");
        }
      } catch {
        // polling errors are transient, keep trying
      }
    }, POLL_INTERVAL_MS);
    expiryRef.current = setTimeout(() => {
      stopPolling();
      setError("Verification code expired. Please request a new one.");
    }, OTP_EXPIRATION_MS);
  }, [stopPolling, onVerified]);
  const handleInitiate = async () => {
    if (!phoneInput || !isValidPhoneNumber(phoneInput)) {
      const msg = "Enter a valid phone number";
      setError(msg);
      onError?.(msg);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result_0 = await initiateSmsVerify(phoneInput);
      setVerifyData(result_0);
      setStep("waiting");
      startCooldown();
      startPolling(phoneInput);
    } catch (err) {
      const msg_0 = err instanceof Error ? err.message : "Failed to start verification";
      setError(msg_0);
      onError?.(msg_0);
    } finally {
      setLoading(false);
    }
  };
  const handleReset = () => {
    stopPolling();
    setStep("phone");
    setVerifyData(null);
    setError(null);
  };
  const handlePhoneChange = (value: Value | undefined) => {
    setPhoneInput(value);
    setError(null);
    if (value) {
      onPhoneChange?.(value);
    }
  };
  if (step === "waiting" && verifyData) {
    const smsDeepLink = `sms:${verifyData.sendToPhone}?body=${encodeURIComponent(verifyData.code)}`;
    return <div className="space-y-3">
        <div className="border-ds-border-default bg-ds-surface-secondary rounded-ds-md space-y-2 border p-4">
          <p className="body-sm font-medium">
            Text this code from{" "}
            <span className="font-mono">{phoneInput ? formatPhoneNumberIntl(phoneInput) : phoneInput}</span>:
          </p>
          <div className="flex items-center justify-center gap-3 py-2">
            <span className="font-mono text-2xl font-bold tracking-[0.3em]">{verifyData.code}</span>
          </div>
          <p className="body-sm text-ds-text-muted text-center">
            Send to <span className="font-mono font-medium">{verifyData.sendToPhone}</span>
          </p>
          <div className="flex justify-center pt-1">
            <a href={smsDeepLink} className="rounded-ds-sm bg-ds-brand-500 hover:bg-ds-brand-600 inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white transition-colors">
              Open Messages
            </a>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <PixelLoader size="sm" />
            <span className="caption-xs text-ds-text-muted">Waiting for your message...</span>
          </div>
          <button type="button" onClick={handleReset} className="caption-xs text-ds-brand-500 hover:underline">
            Cancel
          </button>
        </div>

        <button type="button" onClick={handleInitiate} disabled={loading || disabled || cooldown > 0} className="caption-xs text-ds-text-muted hover:underline disabled:cursor-not-allowed disabled:opacity-50">
          {cooldown > 0 ? `Get new code (${cooldown}s)` : "Get new code"}
        </button>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>;
  }
  return <div className="space-y-1.5">
      <div className="border-ds-border-default bg-ds-surface-sunken flex h-10 flex-nowrap items-center overflow-hidden rounded-md border transition-colors focus-within:border-[var(--ds-focus-brand,var(--ds-brand-500))]">
        <div className="sms-phone-input min-w-0 flex-1 overflow-hidden pl-3">
          <PhoneInput international defaultCountry="US" value={phoneInput} onChange={handlePhoneChange} disabled={loading || disabled} />
        </div>
        <button type="button" onClick={handleInitiate} disabled={loading || disabled || !phoneInput} className="bg-ds-brand-500 hover:bg-ds-brand-600 flex h-full shrink-0 items-center px-4 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:bg-transparent disabled:text-[var(--ds-text-muted)] disabled:opacity-40">
          {loading ? <PixelLoader size="sm" /> : "Verify"}
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>;
}